import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getPersonalInfo, updatePersonalInfo } from '../shared/_api'
import { useInvestDataState } from './_investDataState'
import { IndividualAccountTypes } from '../shared/_types'

export const personalInfoQueryKeyRoot = ['personalInfo'] as const

export const getPersonalInfoQueryKey = (opportunityId?: number) =>
  [...personalInfoQueryKeyRoot, opportunityId] as const

export const usePersonalInfoDataState = () => {
  const { data: investData } = useInvestDataState()

  return useQuery({
    queryKey: getPersonalInfoQueryKey(investData?.opportunity?.id),
    queryFn: async () => {
      const data = await getPersonalInfo(investData.opportunity.id)
      return {
        ...data,
      }
    },
    staleTime: Infinity,
  })
}

export const usePersonalInfoMutation = () => {
  const { data: investData } = useInvestDataState()
  const queryClient = useQueryClient()
  const personalInfoQueryKey = getPersonalInfoQueryKey(
    investData?.opportunity?.id
  )

  return useMutation({
    mutationFn: (data) => updatePersonalInfo(investData?.opportunity?.id, data),
    onMutate: async (newPersonalInfo: IndividualAccountTypes) => {
      await queryClient.cancelQueries({
        queryKey: personalInfoQueryKey,
      })
      const previousPersonalInfo =
        queryClient.getQueryData<IndividualAccountTypes>(personalInfoQueryKey)
      if (previousPersonalInfo) {
        queryClient.setQueryData<IndividualAccountTypes>(personalInfoQueryKey, {
          ...previousPersonalInfo,
          ...newPersonalInfo,
        })
      }
      return { previousPersonalInfo }
    },
    onSuccess: (data: IndividualAccountTypes) =>
      Promise.all([
        queryClient.setQueryData<IndividualAccountTypes>(personalInfoQueryKey, {
          ...data,
        }),
        queryClient.invalidateQueries({
          queryKey: personalInfoQueryKeyRoot,
        }),
        queryClient.invalidateQueries({
          queryKey: ['invest'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['account'],
        }),
      ]),
    onError: (_err: any, _variables: any, context) => {
      if (context?.previousPersonalInfo) {
        queryClient.setQueryData<IndividualAccountTypes>(
          personalInfoQueryKey,
          context.previousPersonalInfo
        )
      }
    },
  })
}
